import React, { useState, useEffect } from 'react';
import ColorInit from "../helper/ColorInit";
import HeaderOne from "../components/HeaderTwo";
import BottomFooter from "../components/BottomFooter";

const PrivacyPolicy = () => {
    const [policyData, setPolicyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedSections, setExpandedSections] = useState({
        info_collection: true,
        personal_data: false,
        usage_data: false,
        tracking_cookies: false,
        use_of_data: false,
        transfer_data: false,
        disclosure: false,
        security: false,
        service_providers: false,
        analytics: false,
        links: false,
        litespeed: false,
        mailpoet: false,
        akismet: false,
        children: false,
        changes: false
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchPolicyData();
    }, []);

    const fetchPolicyData = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/privacy-policy/latest/`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch privacy policy: ${response.status}`);
            }

            const data = await response.json();
            setPolicyData(data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching privacy policy:', err);
            setError('Failed to load privacy policy');
            setLoading(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const toggleAllSections = (expand) => {
        const allSections = Object.keys(expandedSections).reduce((acc, key) => {
            acc[key] = expand;
            return acc;
        }, {});
        setExpandedSections(allSections);
    };

    const renderList = (text) => {
        if (!text) return null;
        return text.split('\n').filter(item => item.trim()).map((item, index) => (
            <li key={index} style={{ marginBottom: "10px", color: "#6B7280", paddingLeft: "10px" }}>{item.trim()}</li>
        ));
    };

    const palette = {
        midnight: "#1D2A3A",
        slate: "#2C3E50",
        deepSlate: "#111827",
        lightSlate: "#334155",
        accentNavy: "#1e293b",
        bgOffWhite: "#F8FAFC"
    };

    if (loading) {
        return (
            <>
                <HeaderOne />
                <div style={{ textAlign: "center", padding: "100px", backgroundColor: palette.bgOffWhite, minHeight: "60vh" }}>
                    <p style={{ fontSize: "1.5rem", color: palette.midnight }}>Loading privacy policy...</p>
                </div>
                <BottomFooter />
            </>
        );
    }

    if (error || !policyData) {
        return (
            <>
                <HeaderOne />
                <div style={{ textAlign: "center", padding: "100px", backgroundColor: palette.bgOffWhite, minHeight: "60vh" }}>
                    <p style={{ fontSize: "1.5rem", color: palette.midnight }}>{error || "No privacy data available."}</p>
                </div>
                <BottomFooter />
            </>
        );
    }

    const sectionLinkStyle = {
        color: palette.midnight,
        textDecoration: "none",
        fontWeight: "600",
        transition: "color 0.3s ease"
    };

    const cardStyle = (expanded) => ({
        backgroundColor: "#FFFFFF",
        borderRadius: "20px",
        marginBottom: "24px",
        border: `1px solid #E2E8F0`,
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        cursor: "pointer",
        position: "relative"
    });

    const headerStyle = (expanded) => ({
        backgroundColor: expanded ? "#F1F5F9" : "#FFFFFF",
        padding: "24px 32px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        transition: "background-color 0.3s ease"
    });

    const titleStyle = (expanded) => ({
        color: expanded ? palette.midnight : "#1A1A1A",
        margin: "0",
        fontSize: "1.35rem",
        fontWeight: "600",
        display: "flex",
        alignItems: "center",
        gap: "15px"
    });

    const contentStyle = (expanded) => ({
        padding: expanded ? "32px" : "0",
        maxHeight: expanded ? "5000px" : "0",
        opacity: expanded ? "1" : "0",
        overflow: "hidden",
        transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        color: "#4B5563",
        fontSize: "16px",
        lineHeight: "1.8"
    });

    const buttonStyle = {
        backgroundColor: palette.midnight,
        color: "white",
        border: "none",
        padding: "12px 24px",
        borderRadius: "12px",
        cursor: "pointer",
        fontWeight: "600",
        fontSize: "14px",
        transition: "all 0.3s ease",
        boxShadow: "0 4px 15px rgba(29, 42, 58, 0.2)"
    };

    return (
        <>
            <HeaderOne />
            <ColorInit color={true} />

            <div style={{ backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
                {/* Hero Section */}
                <section style={{
                    background: `linear-gradient(135deg, ${palette.midnight} 0%, ${palette.slate} 100%)`,
                    padding: "80px 20px",
                    textAlign: "center",
                    position: "relative",
                    overflow: "hidden",
                    marginBottom: "40px"
                }}>
                    <div style={{
                        position: "absolute",
                        top: "-50%",
                        left: "-20%",
                        width: "140%",
                        height: "200%",
                        background: "radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)",
                        pointerEvents: "none"
                    }}></div>
                    <div style={{ maxWidth: "900px", margin: "0 auto", position: "relative", zIndex: 2 }}>
                        <h1 style={{
                            fontSize: "clamp(2.5rem, 5vw, 4rem)",
                            fontWeight: "800",
                            marginBottom: "15px",
                            color: "white",
                            textShadow: "0 2px 10px rgba(0,0,0,0.1)"
                        }}>
                            {policyData.page_title}
                        </h1>
                        <p style={{
                            fontSize: "1.1rem",
                            color: "rgba(255,255,255,0.95)",
                            fontWeight: "500"
                        }}>
                            Effective date: {policyData.effective_date}
                        </p>
                    </div>
                </section>

                <div style={{ maxWidth: "1100px", margin: "20px auto 0", padding: "0 20px 100px", position: "relative", zIndex: 3 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "40px", flexWrap: "wrap", gap: "20px" }}>
                        <div style={{
                            color: "#1A1A1A",
                            fontSize: "2.2rem",
                            fontWeight: "700",
                            paddingLeft: "10px",
                            borderLeft: `8px solid ${palette.midnight}`
                        }}>
                            Privacy Details
                        </div>
                        <div style={{ display: "flex", gap: "15px" }}>
                            <button
                                onClick={() => toggleAllSections(true)}
                                style={buttonStyle}
                                onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                                onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                            >
                                Expand All
                            </button>
                            <button
                                onClick={() => toggleAllSections(false)}
                                style={{ ...buttonStyle, backgroundColor: "#F1F5F9", color: "#64748B", border: "1px solid #E2E8F0" }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                                onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                            >
                                Collapse All
                            </button>
                        </div>
                    </div>

                    {/* Section Builder */}
                    {[
                        { id: 'info_collection', title: policyData.info_collection_title, text: policyData.info_collection_text, subtitle: policyData.types_of_data_title },
                        { id: 'personal_data', title: policyData.personal_data_title, text: policyData.personal_data_text, list: policyData.personal_data_list },
                        { id: 'usage_data', title: policyData.usage_data_title, text: policyData.usage_data_text },
                        { id: 'tracking_cookies', title: policyData.tracking_cookies_title, text: [policyData.tracking_cookies_text1, policyData.tracking_cookies_text2, policyData.tracking_cookies_text3], list: policyData.tracking_cookies_examples },
                        { id: 'use_of_data', title: policyData.use_of_data_title, text: "Panda Print uses the collected data for various purposes:", list: policyData.use_of_data_list },
                        { id: 'transfer_data', title: policyData.transfer_data_title, text: [policyData.transfer_data_text1, policyData.transfer_data_text2, policyData.transfer_data_text3, policyData.transfer_data_text4] },
                        { id: 'disclosure', title: policyData.disclosure_title, subtitle: policyData.disclosure_subtitle, text: "Panda Print may disclose your Personal Data in the good faith belief that such action is necessary to:", list: policyData.disclosure_list },
                        { id: 'security', title: policyData.security_title, text: policyData.security_text },
                        { id: 'service_providers', title: policyData.service_providers_title, text: [policyData.service_providers_text1, policyData.service_providers_text2], analytics: true },
                        { id: 'changes', title: policyData.changes_title, text: [policyData.changes_text1, policyData.changes_text2, policyData.changes_text3], contact: true }
                    ].map((section) => (
                        <div
                            key={section.id}
                            style={cardStyle(expandedSections[section.id])}
                            onClick={() => toggleSection(section.id)}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-5px)";
                                e.currentTarget.style.boxShadow = "0 20px 40px rgba(29, 42, 58, 0.1)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 10px 30px rgba(0, 0, 0, 0.05)";
                            }}
                        >
                            <div style={headerStyle(expandedSections[section.id])}>
                                <h3 style={titleStyle(expandedSections[section.id])}>
                                    <span style={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "10px",
                                        backgroundColor: expandedSections[section.id] ? palette.midnight : "#F1F5F9",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        transition: "all 0.3s ease"
                                    }}>
                                        <i className={`ph ph-caret-${expandedSections[section.id] ? 'up' : 'down'}`} style={{ fontSize: "16px", color: expandedSections[section.id] ? "white" : "#64748B" }}></i>
                                    </span>
                                    {section.title}
                                </h3>
                            </div>
                            <div style={contentStyle(expandedSections[section.id])}>
                                {Array.isArray(section.text) ? section.text.map((t, i) => <p key={i} style={{ marginBottom: "15px" }}>{t}</p>) : <p style={{ marginBottom: "15px" }}>{section.text}</p>}
                                {section.subtitle && <h4 style={{ color: palette.midnight, margin: "25px 0 15px", fontSize: "1.2rem", fontWeight: "700" }}>{section.subtitle}</h4>}
                                {section.list && <ul style={{ listStyleType: "none", padding: 0, marginTop: "20px" }}>{renderList(section.list)}</ul>}

                                {section.analytics && policyData.analytics_title && (
                                    <div style={{ marginTop: "30px", padding: "25px", backgroundColor: palette.bgOffWhite, borderRadius: "15px" }}>
                                        <h4 style={{ color: palette.midnight, marginBottom: "15px" }}>{policyData.analytics_title}</h4>
                                        <p style={{ color: palette.slate, fontWeight: "700" }}>{policyData.google_analytics_title}</p>
                                        <p>{policyData.google_analytics_text1}</p>
                                    </div>
                                )}

                                {section.contact && (
                                    <div style={{ marginTop: "40px", padding: "30px", borderTop: `2px solid #F1F5F9`, textAlign: "center" }}>
                                        <p style={{ fontSize: "1.1rem" }}>
                                            Questions? Contact us at <br />
                                            <a href="mailto:privacy@pandaprint.com" style={{ ...sectionLinkStyle, fontSize: "1.3rem", color: palette.midnight }}>privacy@pandaprint.com</a>
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <BottomFooter />
        </>
    );
};

export default PrivacyPolicy;